import { Grid, Typography } from '@mui/material';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { FormFieldWrapper } from '@procureai/ui-form-mui';
import { DateFormatPreset, formatDate } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { DemandDetailQuery, TsrctCompetitiveBiddingVariant } from '@/generated/gql/graphql';
import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { CuriosityInfoChip } from '~/components/CuriosityInfoChip/CuriosityInfoChip';
import { CuriosityUserChip } from '~/components/CuriosityUserChip/CuriosityUserChip';
import { ListEmptyFallback } from '~/components/ListEmptyFallback/ListEmptyFallback';

import { StackStyled } from './DemandDetailContent.styled';

export type DemandDetailContentData = Pick<
  DemandDetailQuery['tesseract']['demand'],
  'capabilityIds' | 'responsibleUserIds' | 'competitiveBiddingRule' | 'demandRequest'
>;

export interface DemandDetailContentProps {
  data?: Union.Nullable<DemandDetailContentData>;
}

export const DemandDetailContent = ({ data }: DemandDetailContentProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandDetailWidget' });
  const competitiveBiddingDeadline = data?.competitiveBiddingRule?.deadline;
  const maskBidsChecked = data?.competitiveBiddingRule?.variant === TsrctCompetitiveBiddingVariant.MASKED;
  const responsibleUserIds = data?.responsibleUserIds?.filter((item): item is string => item != null);
  const capabilityIds = data?.capabilityIds?.filter((item): item is string => item != null);

  return (
    <Grid size={12} container spacing={3}>
      <FormFieldWrapper fullWidth label={t('responsibles')}>
        <ListEmptyFallback component={StackStyled} direction="row">
          {responsibleUserIds?.map((item, index) => (
            <CuriosityUserChip key={item ?? index} userId={item} />
          ))}
        </ListEmptyFallback>
      </FormFieldWrapper>
      <FormFieldWrapper fullWidth label={t('capabilities')}>
        <ListEmptyFallback component={StackStyled} direction="row">
          {capabilityIds?.map((item, index) => (
            <CuriosityInfoChip key={item ?? index} ns={CuriosityNamespaceType.CAPABILITY} i18nKey={item} />
          ))}
        </ListEmptyFallback>
      </FormFieldWrapper>
      <Grid size={12}>
        <Typography paddingTop={3} variant="subtitle1" color="textPrimary">
          {t('competitiveBidding.title')}
        </Typography>
      </Grid>
      <FormFieldWrapper fullWidth label={t('competitiveBidding.deadline')}>
        <Typography variant="body2">
          {competitiveBiddingDeadline
            ? formatDate(DateFormatPreset.TWO_DIGIT_TIME, new Date(competitiveBiddingDeadline))
            : NULLISH_VALUE}
        </Typography>
      </FormFieldWrapper>
      <FormFieldWrapper fullWidth label={t('competitiveBidding.maskBidding.label')}>
        <Typography variant="body2">{t(`competitiveBidding.maskBidding.${maskBidsChecked}`)}</Typography>
      </FormFieldWrapper>
    </Grid>
  );
};
